import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  usePublishNegotiationRoundMutation,
  type NegotiationRoundDto,
  type SourcingEventDto,
} from "../../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../../common/documents/DocumentsAsOwnerTable";
import { isApiErrorDto } from "../../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { StepNavigation } from "../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import {
  selectionJustificationMissing,
  useDialogueRoster,
} from "../../rounds/useDialogueRoster";
import { RoundInvitesList } from "../RoundInvitesList";

interface Props {
  event: SourcingEventDto;
  round: NegotiationRoundDto;
  previousStep?: () => void;
  onPublished: () => void;
}

export const RoundReviewStep = ({
  event,
  round,
  previousStep,
  onPublished,
}: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const displayDateWithTime = useDisplayDateWithTime();
  const confirmationModal = useConfirmationModal();
  const [publishRound, { isLoading: isPublishing }] =
    usePublishNegotiationRoundMutation();
  const [refusedAsEvaluationCompleted, setRefusedAsEvaluationCompleted] =
    useState(false);

  const isDraft = round.status === NegotiationRoundStatus.Draft;
  const deadlineHasPassed = isDraft && !isDeadlineAhead(round.deadline);
  const roster = useDialogueRoster(event, round);
  const justificationMissing =
    isDraft &&
    selectionJustificationMissing(roster, round.selectionJustification);

  const handlePublish = () => {
    confirmationModal.show({
      title: t("Publish negotiation round?"),
      description: round.isFinalRound
        ? t(
            "This is the final round. Invited suppliers are notified of the round, and its round card shows them it is the final one. No further rounds can be started afterwards. This cannot be undone.",
          )
        : t("Invited suppliers will be notified. This cannot be undone."),
      confirmButtonText: t("Publish") ?? "Publish",
      onConfirmed: async () => {
        try {
          await publishRound({
            eventId: round.eventId,
            roundId: round.id,
          }).unwrap();
          toast({ title: t("Negotiation round published"), status: "success" });
          onPublished();
        } catch (e) {
          if (isEvaluationCompletedRefusal(e))
            setRefusedAsEvaluationCompleted(true);
          else trigger(e);
        }
      },
    });
  };

  return (
    <Box pb="8" pt="10">
      {confirmationModal.modal}
      <Heading as="h2" size="md" mb="6">
        {t("Review and publish")}
      </Heading>

      <Stack spacing="6" maxW="3xl">
        <Stack spacing="1">
          <SummaryRow
            label={t("Title")}
            value={
              round.title || `${t("Negotiation round")} ${round.roundNumber}`
            }
          />
          <SummaryRow
            label={t("Bid deadline")}
            value={displayDateWithTime(round.deadline)}
          />
          <SummaryRow
            label={t("Final Round")}
            value={round.isFinalRound ? t("Yes") : t("No")}
          />
        </Stack>

        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium">
            {t("Documents")} ({round.documents.length})
          </Text>
          {round.documents.length > 0 ? (
            <DocumentsAsOwnerTable documents={round.documents} />
          ) : (
            <Text fontSize="sm" color="smMuted">
              {t("No documents")}
            </Text>
          )}
        </Stack>

        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium">
            {t("Invited organizations")} ({round.invites.length})
          </Text>
          <RoundInvitesList
            invites={round.invites}
            rejectedOrganizationIds={roster.rejectedOrganizationIds}
          />
        </Stack>

        {isDraft ? (
          <Stack spacing="3">
            {deadlineHasPassed && (
              <Alert status="warning" rounded="lg">
                <AlertIcon />
                <AlertDescription>
                  {t(
                    "The round's bid deadline has passed. Go back and choose a deadline in the future to publish it.",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {justificationMissing && (
              <Alert status="warning" rounded="lg">
                <AlertIcon />
                <AlertDescription>
                  {t(
                    "Not every supplier who tendered is invited. Go back to Invitations and write why, to publish the round (FOA § 9-3 (2)).",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {refusedAsEvaluationCompleted && (
              <Alert status="error" rounded="lg">
                <AlertIcon />
                <AlertDescription>{t(EVALUATION_COMPLETED)}</AlertDescription>
              </Alert>
            )}
            <Flex>
              <Button
                colorScheme="teal"
                onClick={handlePublish}
                isLoading={isPublishing}
                isDisabled={
                  round.invites.length === 0 ||
                  deadlineHasPassed ||
                  justificationMissing ||
                  refusedAsEvaluationCompleted
                }
              >
                {t("Publish round")}
              </Button>
            </Flex>
          </Stack>
        ) : (
          <Alert status="info" rounded="lg">
            <AlertIcon />
            <AlertDescription>
              {t(
                "The round is published, and changes are immediately visible to invited suppliers.",
              )}
            </AlertDescription>
          </Alert>
        )}
      </Stack>

      <StepNavigation onPrevious={previousStep} />
    </Box>
  );
};

const EVALUATION_COMPLETED =
  "zz_SourcingEventApiErrorCode_CannotOpenNegotiationRoundAfterEvaluationCompleted";

const isEvaluationCompletedRefusal = (e: unknown): boolean =>
  isFetchBaseQueryError(e) &&
  isApiErrorDto(e.data) &&
  e.data.errorCode === EVALUATION_COMPLETED;

export const isDeadlineAhead = (
  deadline: string,
  now: Date = new Date(),
): boolean => new Date(deadline).getTime() > now.getTime();

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Flex gap="3">
    <Text fontSize="sm" color="smMuted" minW="40">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {value}
    </Text>
  </Flex>
);
